import { useRef, forwardRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

/**
 * Reveal — fades + slides content in as it enters viewport.
 * Triggers once. Respects prefers-reduced-motion.
 *
 * <Reveal>           default fade-up
 * <Reveal y={40}>    slide further
 * <Reveal delay={0.2}>
 * <Reveal direction="left">
 * <Reveal as="section" stagger=0.08>  staggers children with the same Reveal
 */
export const Reveal = forwardRef(({
  children,
  delay = 0,
  duration = 0.6,
  y,
  x = 0,
  direction = "up",
  margin = "-80px",
  once = true,
  className,
  style,
  as = "div",
  ...rest
}, externalRef) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const setRefs = (node) => {
    ref.current = node;
    if (typeof externalRef === "function") externalRef(node);
    else if (externalRef) externalRef.current = node;
  };
  const inView = useInView(ref, { once, margin });

  // Direction mapping
  const dy = y !== undefined ? y : (direction === "up" ? 28 : direction === "down" ? -28 : 0);
  const dx = direction === "left" ? -28 : direction === "right" ? 28 : x;

  if (reduce) {
    const Cmp = motion[as] || motion.div;
    return <Cmp ref={setRefs} className={className} style={style} {...rest}>{children}</Cmp>;
  }

  const Cmp = motion[as] || motion.div;

  return (
    <Cmp
      ref={setRefs}
      className={className}
      style={style}
      initial={{ opacity: 0, y: dy, x: dx }}
      animate={inView ? { opacity: 1, y: 0, x: 0 } : { opacity: 0, y: dy, x: dx }}
      transition={{
        duration,
        delay,
        ease: [0.22, 1, 0.36, 1], // premium ease-out (expo-like)
      }}
      {...rest}
    >
      {children}
    </Cmp>
  );
});

Reveal.displayName = "Reveal";

/**
 * RevealGroup — staggers a list of children with the same Reveal feel.
 * Use when you have a card grid that should cascade in.
 */
export function RevealGroup({ children, stagger = 0.08, delay = 0, ...props }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  if (reduce) {
    return <div ref={ref} {...props}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
      {...props}
    >
      {Array.isArray(children)
        ? children.map((child, i) => (
            <motion.div
              key={child?.key ?? i}
              variants={{
                hidden: { opacity: 0, y: 22 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
              }}
            >
              {child}
            </motion.div>
          ))
        : (
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 22 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
              }}
            >
              {children}
            </motion.div>
          )}
    </motion.div>
  );
}
